/**
 * Secret Easter Egg Page - Spot the Wonder Dog
 * A hidden page about a dog who lived to 100 thanks to dental care!
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BookAppointmentLink from '../components/BookAppointmentLink';
import { log } from '../utils/log';

const SecretSpot: React.FC = () => {
  const [gameActive, setGameActive] = useState(false);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [teeth, setTeeth] = useState<Array<{ id: number; x: number; y: number; extracted: boolean }>>([]);
  const [gameOver, setGameOver] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isExtracting, setIsExtracting] = useState(false);

  // Initialize teeth positions
  useEffect(() => {
    const newTeeth = Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x: 100 + (i % 6) * 120,
      y: 150 + Math.floor(i / 6) * 100,
      extracted: false,
    }));
    setTeeth(newTeeth);
  }, []);

  // Game timer
  useEffect(() => {
    if (gameActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && gameActive) {
      setGameActive(false);
      setGameOver(true);
    }
  }, [gameActive, timeLeft]);

  const startGame = () => {
    setGameActive(true);
    setScore(0);
    setTimeLeft(30);
    setGameOver(false);
    const newTeeth = Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x: 100 + (i % 6) * 120,
      y: 150 + Math.floor(i / 6) * 100,
      extracted: false,
    }));
    setTeeth(newTeeth);
  };

  const extractTooth = (toothId: number) => {
    if (!gameActive) return;
    
    setIsExtracting(true);
    setTimeout(() => setIsExtracting(false), 200);
    
    setTeeth(teeth.map(tooth => 
      tooth.id === toothId ? { ...tooth, extracted: true } : tooth
    ));
    setScore(score + 100);
    
    // Fun sound effect (could add actual audio)
    log('🦷 TOOTH EXTRACTED! +100 points');
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setCursorPosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-100 via-pink-100 to-blue-100 relative overflow-hidden">
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="absolute animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 10}s`,
            }}
          >
            {['🦴', '🦷', '⭐', '🎉', '🐕'][Math.floor(Math.random() * 5)]}
          </div>
        ))}
      </div>

      {/* Back button */}
      <div className="absolute top-4 left-4 z-50">
        <Link
          to="/team"
          className="bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg hover:bg-white transition-all text-sm font-medium text-gray-700 hover:text-brand-teal"
        >
          ← Shh... Go Back
        </Link>
      </div>

      <div className="container-custom section-padding relative z-10">
        {/* Header */}
        <div className="text-center mb-12 animate-bounce-slow">
          <div className="inline-block">
            <div className="text-8xl mb-4 animate-wiggle">🐕</div>
            <h1 className="text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 mb-4">
              🎉 MEET SPOT! 🎉
            </h1>
            <p className="text-2xl font-bold text-purple-600 mb-2">
              The Dog Who Lived to 100!
            </p>
            <p className="text-lg text-gray-600 italic">
              (Yes, that's 700 in dog years! 🤯)
            </p>
          </div>
        </div>

        {/* Spot's Story */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border-4 border-purple-300">
            {/* Cartoon Spot */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                {/* Dog body */}
                <div className="w-64 h-64 bg-amber-600 rounded-full relative shadow-xl">
                  {/* Head */}
                  <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-32 h-32 bg-amber-600 rounded-full">
                    {/* Ears */}
                    <div className="absolute -left-8 top-4 w-16 h-24 bg-amber-700 rounded-full transform -rotate-12"></div>
                    <div className="absolute -right-8 top-4 w-16 h-24 bg-amber-700 rounded-full transform rotate-12"></div>
                    
                    {/* Eyes */}
                    <div className="absolute left-6 top-8 w-8 h-8 bg-white rounded-full">
                      <div className="absolute left-2 top-2 w-4 h-4 bg-black rounded-full animate-blink"></div>
                    </div>
                    <div className="absolute right-6 top-8 w-8 h-8 bg-white rounded-full">
                      <div className="absolute left-2 top-2 w-4 h-4 bg-black rounded-full animate-blink"></div>
                    </div>
                    
                    {/* Nose */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-16 w-6 h-6 bg-black rounded-full"></div>
                    
                    {/* HUGE SMILE with perfect teeth */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-20 w-20 h-10 border-4 border-black rounded-b-full bg-pink-300">
                      {/* Perfect white teeth */}
                      <div className="flex justify-around mt-1">
                        {Array.from({ length: 6 }).map((_, i) => (
                          <div key={i} className="w-2 h-3 bg-white rounded-sm border border-gray-300"></div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Spots on body */}
                  <div className="absolute top-12 left-8 w-12 h-12 bg-amber-800 rounded-full"></div>
                  <div className="absolute top-24 right-12 w-16 h-16 bg-amber-800 rounded-full"></div>
                  <div className="absolute bottom-20 left-16 w-10 h-10 bg-amber-800 rounded-full"></div>
                  
                  {/* Legs */}
                  <div className="absolute -bottom-8 left-12 w-8 h-16 bg-amber-600 rounded-lg"></div>
                  <div className="absolute -bottom-8 right-12 w-8 h-16 bg-amber-600 rounded-lg"></div>
                  
                  {/* Tail wagging */}
                  <div className="absolute right-0 top-1/2 w-20 h-6 bg-amber-600 rounded-full transform rotate-45 origin-left animate-wag"></div>
                </div>
                
                {/* Party hat */}
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-0 h-0 border-l-16 border-r-16 border-b-24 border-l-transparent border-r-transparent border-b-purple-500">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-6 bg-yellow-400 rounded-full"></div>
                </div>
              </div>
            </div>

            {/* Story text */}
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p className="text-2xl font-bold text-center text-purple-600">
                🌟 The Legendary Tale of Spot 🌟
              </p>
              
              <p>
                Once upon a time in sunny Florida, there lived a Golden Retriever named Spot. 
                At the ripe young age of 10, Spot had terrible breath that could knock out a 
                palm tree from 50 feet away. 🌴💨
              </p>
              
              <p>
                His teeth were in rough shape - tartar buildup so thick you could use it as 
                armor, and gums redder than a Miami sunset. His humans were worried. Would 
                Spot make it to see another year?
              </p>
              
              <p className="text-xl font-bold text-center text-brand-teal">
                Then... he discovered CHOMP Pet Dental! 🦷✨
              </p>
              
              <p>
                Dr. Heller and Dr. Machek worked their magic. They cleaned Spot's teeth, 
                extracted a few troublemakers, and gave him a smile that would make a 
                Hollywood star jealous. 😁
              </p>
              
              <p>
                The result? Spot didn't just live another year... he lived another 
                <span className="text-4xl font-black text-pink-500"> NINETY YEARS! </span>
                That's right - Spot celebrated his 100th birthday last Tuesday!
              </p>
              
              <div className="bg-yellow-50 border-4 border-yellow-400 rounded-2xl p-6 text-center">
                <p className="text-xl font-bold mb-2">🎂 Spot's Current Activities (Age 100) 🎂</p>
                <ul className="space-y-2">
                  <li>🏖️ Beach volleyball champion (7 years running)</li>
                  <li>🎸 Lead singer in a doggy rock band</li>
                  <li>🏄‍♂️ Professional surfing instructor</li>
                  <li>👨‍🍳 Judges the annual "Best Treat Baker" competition</li>
                  <li>💪 Still has ALL his teeth (well, the ones that count!)</li>
                </ul>
              </div>
              
              <p className="text-center italic text-gray-600">
                "I owe it all to CHOMP and those magical tooth fairies... I mean, veterinarians!" 
                <br />- Spot (through an interpreter)
              </p>
              
              <p className="text-sm text-center text-gray-500 mt-8">
                * Spot's story may or may not be 100% scientifically accurate. 
                But the importance of dental care? That's REAL! 🦷❤️
              </p>
            </div>
          </div>
        </div>

        {/* The Game */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-red-100 to-pink-100 rounded-3xl shadow-2xl p-8 border-4 border-red-300">
            <h2 className="text-4xl font-black text-center text-red-600 mb-4">
              🎮 TOOTH EXTRACTION CHALLENGE! 🎮
            </h2>
            <p className="text-center text-gray-700 mb-6">
              Help Dr. Spot extract bad teeth! Click the teeth before time runs out!
              <br />
              <span className="text-sm italic">(No dogs were harmed in the making of this game)</span>
            </p>

            {!gameActive && !gameOver && (
              <div className="text-center">
                <button
                  onClick={startGame}
                  className="bg-gradient-to-r from-pink-500 to-purple-500 text-white px-12 py-6 rounded-full text-2xl font-bold shadow-xl hover:scale-110 transition-transform hover:shadow-2xl"
                >
                  🦷 START EXTRACTION! 🦷
                </button>
              </div>
            )}

            {gameActive && (
              <div className="space-y-6">
                <div className="flex justify-between items-center text-2xl font-bold">
                  <div className="bg-white px-6 py-3 rounded-full shadow-lg">
                    ⏱️ Time: {timeLeft}s
                  </div>
                  <div className="bg-white px-6 py-3 rounded-full shadow-lg">
                    🏆 Score: {score}
                  </div>
                </div>

                <div
                  className="relative bg-pink-200 rounded-2xl h-96 overflow-hidden cursor-crosshair"
                  onMouseMove={handleMouseMove}
                  style={{
                    cursor: isExtracting ? 'grabbing' : 'grab',
                  }}
                >
                  {/* Dog's mouth */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-9xl opacity-20">👄</div>
                  </div>

                  {/* Teeth */}
                  {teeth.map((tooth) => (
                    !tooth.extracted && (
                      <div
                        key={tooth.id}
                        onClick={() => extractTooth(tooth.id)}
                        className="absolute cursor-pointer hover:scale-125 transition-transform"
                        style={{
                          left: `${tooth.x}px`,
                          top: `${tooth.y}px`,
                          transform: `rotate(${Math.random() * 20 - 10}deg)`,
                        }}
                      >
                        <div className="text-4xl hover:animate-wiggle">🦷</div>
                      </div>
                    )
                  ))}

                  {/* Custom cursor (pliers) */}
                  {gameActive && (
                    <div
                      className="absolute pointer-events-none text-3xl"
                      style={{
                        left: `${cursorPosition.x}px`,
                        top: `${cursorPosition.y}px`,
                        transform: 'translate(-50%, -50%)',
                      }}
                    >
                      🔧
                    </div>
                  )}
                </div>

                <p className="text-center text-gray-600 text-sm italic">
                  💡 Pro tip: In real life, we use anesthesia and precision instruments, not your mouse! 😄
                </p>
              </div>
            )}

            {gameOver && (
              <div className="text-center space-y-6">
                <div className="text-6xl">
                  {score >= 1000 ? '🏆' : score >= 500 ? '⭐' : '😅'}
                </div>
                <h3 className="text-3xl font-bold">
                  {score >= 1000 ? 'LEGENDARY DENTIST!' : 
                   score >= 500 ? 'Great Job!' : 
                   'Keep Practicing!'}
                </h3>
                <p className="text-2xl font-bold">Final Score: {score}</p>
                <p className="text-gray-600">
                  {score >= 1000 ? 'Spot would hire you in a heartbeat!' :
                   score >= 500 ? 'You extracted {Math.floor(score/100)} teeth!' :
                   'Even Spot had to start somewhere!'}
                </p>
                <button
                  onClick={startGame}
                  className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-8 py-4 rounded-full text-xl font-bold shadow-xl hover:scale-110 transition-transform"
                >
                  🔄 Try Again!
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Bottom message */}
        <div className="text-center mt-12">
          <p className="text-lg text-gray-600 italic">
            Pssst... you found the secret page! 🤫
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Real dental care = Real results. Book your pet's appointment today!
          </p>
          <BookAppointmentLink
            className="inline-block mt-4 bg-brand-teal text-white px-6 py-3 rounded-full font-bold hover:scale-105 transition-transform"
          >
            Book for Real! →
          </BookAppointmentLink>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(10deg); }
        }
        
        @keyframes wiggle {
          0%, 100% { transform: rotate(-5deg); }
          50% { transform: rotate(5deg); }
        }
        
        @keyframes wag {
          0%, 100% { transform: rotate(45deg); }
          50% { transform: rotate(25deg); }
        }
        
        @keyframes blink {
          0%, 90%, 100% { opacity: 1; }
          95% { opacity: 0; }
        }
        
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        .animate-float {
          animation: float linear infinite;
        }
        
        .animate-wiggle {
          animation: wiggle 0.5s ease-in-out infinite;
        }
        
        .animate-wag {
          animation: wag 0.5s ease-in-out infinite;
        }
        
        .animate-blink {
          animation: blink 4s ease-in-out infinite;
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default SecretSpot;
